import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import './widgets.css';

function parseSheetTimestampAsUtc(value) {
  const raw = String(value || '').trim();
  // Accept common sheet formats: `M/D/YYYY H:mm[:ss]`, with optional trailing `UTC`.
  const cleaned = raw.replace(/\s+UTC$/i, '').trim();
  const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  const [, month, day, year, hour, minute, second] = match;
  const monthNum = Number(month);
  const dayNum = Number(day);
  const yearNum = Number(year);
  const hourNum = Number(hour);
  const minuteNum = Number(minute);
  const secondNum = Number(second || '0');

  if (
    monthNum < 1 || monthNum > 12
    || hourNum < 0 || hourNum > 23
    || minuteNum < 0 || minuteNum > 59
    || secondNum < 0 || secondNum > 59
  ) {
    return null;
  }

  const daysInMonth = new Date(Date.UTC(yearNum, monthNum, 0)).getUTCDate();
  if (dayNum < 1 || dayNum > daysInMonth) return null;

  const utcDate = new Date(
    Date.UTC(
      yearNum,
      monthNum - 1,
      dayNum,
      hourNum,
      minuteNum,
      secondNum
    )
  );
  if (Number.isNaN(utcDate.getTime())) return null;
  return utcDate;
}

export function formatUtcTimestampForLocale(value) {
  const parsed = parseSheetTimestampAsUtc(value);
  if (!parsed) return String(value || '').trim();
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(parsed);
}

function localizeOrderText(value, widgetType) {
  const text = String(value || '');
  const isDisambiguation = widgetType === 'order_match';
  const timestampPrefixes = isDisambiguation
    ? [
      { matches: ['Date and Time Last Updated:', 'Last Updated:'], label: 'Last Updated:' },
      { matches: ['Date and Time Added:', 'Added:'], label: 'Added:' }
    ]
    : [
      { matches: ['Appt start:'], label: 'Appt start:' },
      { matches: ['Appt end:'], label: 'Appt end:' }
    ];
  for (const prefixSet of timestampPrefixes) {
    const matchedPrefix = prefixSet.matches.find((prefix) => text.startsWith(prefix));
    if (matchedPrefix) {
      const raw = text.slice(matchedPrefix.length).trim();
      const localized = formatUtcTimestampForLocale(raw);
      return `${prefixSet.label} ${localized || raw}`;
    }
  }
  return text;
}

function splitLabeledText(value) {
  const text = String(value || '');
  const separator = text.indexOf(':');
  if (separator <= 0) return null;
  return {
    label: text.slice(0, separator),
    value: text.slice(separator + 1).trim()
  };
}

/**
 * OrderCard — renders order info from widget data.
 *
 * Handles widgetTypes: order_result, order_summary, order_match, order_list, order_duplicate
 *
 * Uses the same ChatKit Card children structure as PatientCard.
 */
function OrderCard({ data, widgetType, conversationId, messageId, onConfirmationSubmitted }) {
  const [buttonState, setButtonState] = useState({ phase: 'idle', id: null, actionType: null });
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);
  const children = useMemo(() => data.children || [], [data.children]);
  const statusText = data.status?.text || 'Order';
  const titleChild = children.find((c) => c.type === 'Title');

  const headerClass = widgetType === 'order_result'
    ? 'widget-header-success'
    : widgetType === 'order_duplicate'
      ? 'widget-header-warning'
      : 'widget-header-info';

  const isDisambiguation = widgetType === 'order_match';
  const selectableOrderIds = useMemo(
    () => children
      .filter((child) => child.type === 'Button')
      .filter((child) => {
        const actionType = child.onClickAction?.type || 'order.show_match';
        return actionType === 'order.show_match';
      })
      .map((child) => child.onClickAction?.payload?.order?.uidForOrder || '')
      .filter(Boolean),
    [children]
  );
  const disambiguationStateKey = useMemo(() => {
    // Require messageId to scope persistence to a single prompt instance.
    if (!isDisambiguation || !conversationId || !messageId || selectableOrderIds.length === 0) return null;
    return [
      'kapsa:disambiguation-selection:v1',
      conversationId,
      messageId,
      widgetType,
      selectableOrderIds.join('|')
    ].join(':');
  }, [conversationId, isDisambiguation, messageId, selectableOrderIds, widgetType]);

  useEffect(() => {
    if (!disambiguationStateKey || typeof window === 'undefined') {
      setButtonState((prev) => ((prev.phase === 'idle' && !prev.id) ? prev : { phase: 'idle', id: null, actionType: null }));
      return;
    }
    const persistedId = String(window.sessionStorage.getItem(disambiguationStateKey) || '').trim();
    if (persistedId && selectableOrderIds.includes(persistedId)) {
      setButtonState((prev) => ((prev.phase === 'done' && prev.id === persistedId && prev.actionType === 'order.show_match')
        ? prev
        : { phase: 'done', id: persistedId, actionType: 'order.show_match' }));
      return;
    }
    setButtonState((prev) => ((prev.phase === 'idle' && !prev.id) ? prev : { phase: 'idle', id: null, actionType: null }));
  }, [disambiguationStateKey, selectableOrderIds]);

  useEffect(() => {
    if (!disambiguationStateKey || typeof window === 'undefined') return;
    if (buttonState.phase === 'done' && buttonState.id && buttonState.actionType === 'order.show_match') {
      window.sessionStorage.setItem(disambiguationStateKey, buttonState.id);
      return;
    }
    if (buttonState.phase === 'idle' || buttonState.phase === 'loading') {
      window.sessionStorage.removeItem(disambiguationStateKey);
    }
  }, [buttonState.actionType, buttonState.id, buttonState.phase, disambiguationStateKey]);

  return (
    <div className={`widget-card ${isDisambiguation ? 'widget-card--disambiguation' : ''}`} data-widget-type={widgetType}>
      <div className={`widget-card-status-header ${headerClass}`}>
        <span className="widget-status-dot" />
        <span className="widget-status-text">{statusText}</span>
      </div>

      <div className="widget-card-content">
        {children.map((child, i) => {
          switch (child.type) {
            case 'Title':
              return <h4 key={i} className={`widget-card-title ${isDisambiguation ? 'widget-card-record-title' : ''}`}>{child.value}</h4>;
            case 'Badge':
              return (
                <span key={i} className={`widget-badge widget-badge-${child.color || 'primary'}`}>
                  {child.label}
                </span>
              );
            case 'Text':
              {
                const localizedText = localizeOrderText(child.value, widgetType);
                const labeled = isDisambiguation ? splitLabeledText(localizedText) : null;
              return (
                <p key={i} className={`widget-text ${child.weight === 'semibold' ? 'widget-text-bold' : ''} ${isDisambiguation ? 'widget-text-structured' : ''}`}>
                  {labeled ? <span className="widget-text-label">{labeled.label}:</span> : null}
                  {labeled ? <span className="widget-text-value">{labeled.value}</span> : localizedText}
                </p>
              );
              }
            case 'Caption':
              return <p key={i} className="widget-caption">{child.value}</p>;
            case 'Divider':
              return <hr key={i} className="widget-divider" />;
            case 'Spacer':
              return <div key={i} className="widget-spacer" />;
            case 'Button': {
              const orderId = child.onClickAction?.payload?.order?.uidForOrder || '';
              const actionType = child.onClickAction?.type || 'order.show_match';
              const stateMatch = buttonState.id === orderId && buttonState.actionType === actionType;
              const isLoading = buttonState.phase === 'loading' && stateMatch;
              const isDone = buttonState.phase === 'done' && stateMatch;
              const commandPrefix = actionType === 'order.open_update_form' ? '/open-order-update' : '/select-order';
              const isDisabled = !conversationId || !orderId || buttonState.phase === 'loading' || isDone;
              const loadingLabel = actionType === 'order.open_update_form' ? 'Opening...' : 'Selecting...';
              const doneLabel = actionType === 'order.open_update_form' ? 'Opened' : 'Selected';

              return (
                <button
                  key={i}
                  type="button"
                  className={`widget-btn widget-btn-primary ${isDisambiguation ? 'widget-btn-record' : ''} ${isLoading ? 'is-loading' : ''} ${isDone ? 'is-done' : ''}`}
                  disabled={isDisabled}
                  aria-label={`Select ${titleChild?.value || 'order'}`}
                  onClick={async () => {
                    if (!conversationId || !orderId || isDisabled) return;
                    setButtonState({ phase: 'loading', id: orderId, actionType });
                    try {
                      await sendMessage({
                        variables: {
                          conversationId,
                          content: `${commandPrefix} ${orderId}`
                        }
                      });
                      setButtonState({ phase: 'done', id: orderId, actionType });
                      onConfirmationSubmitted?.();
                    } catch (error) {
                      console.error('Failed to run order action:', error);
                      setButtonState({ phase: 'idle', id: null, actionType: null });
                    }
                  }}
                >
                  {isLoading && <span className="widget-btn-spinner" aria-hidden="true" />}
                  {isLoading ? loadingLabel : isDone ? doneLabel : child.label}
                </button>
              );
            }
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}

export default OrderCard;
